import { motion } from "framer-motion";
import { TIPO_LABEL } from "@/lib/format";

// Nota: se anima SOLO scale/posición (nunca opacity) para que el contenido
// crítico permanezca visible aunque la animación no llegue a completarse.
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};
const item = {
  hidden: { scale: 0.75 },
  show: { scale: 1, transition: { type: "spring", stiffness: 260, damping: 18 } },
};

const Ball = ({ children }) => (
  <motion.div
    variants={item}
    className="flex h-14 w-14 items-center justify-center rounded-full border-2 text-xl font-bold shadow-sm sm:h-16 sm:w-16 sm:text-2xl"
    style={{
      borderColor: "hsl(var(--game-accent))",
      color: "hsl(var(--game-accent))",
      background: "hsl(var(--game-surface))",
    }}
  >
    {children}
  </motion.div>
);

export const GameResult = ({ tipo, resultado, size = "lg" }) => {
  if (resultado === null || resultado === undefined) {
    return <p className="text-muted-foreground">Pendiente de sorteo</p>;
  }

  if (tipo === "lotto" || tipo === "rifas") {
    const nums = resultado;
    return (
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="flex flex-wrap gap-3"
        data-testid="game-result-numbers"
      >
        {nums.map((n, i) => (
          <Ball key={i}>{tipo === "rifas" ? `#${n}` : n}</Ball>
        ))}
      </motion.div>
    );
  }

  if (tipo === "triple") {
    const TripleBlock = ({ label, triple, terminal }) => (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
        <div className="flex gap-2">
          {String(triple ?? "").split("").map((d, i) => (
            <Ball key={i}>{d}</Ball>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">Terminal <strong style={{ color: "hsl(var(--game-accent))" }}>{terminal}</strong></span>
      </div>
    );
    return (
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="flex flex-col gap-5"
        data-testid="game-result-triple"
      >
        <div className="flex flex-wrap gap-8">
          <TripleBlock label="Triple A" triple={resultado.triple_a} terminal={resultado.terminal_a} />
          <TripleBlock label="Triple B" triple={resultado.triple_b} terminal={resultado.terminal_b} />
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Estado</span>
          <span
            className="rounded-full px-3 py-1 text-sm font-bold"
            style={{ background: "hsl(var(--game-surface))", color: "hsl(var(--game-accent))" }}
            data-testid="game-result-triple-estado"
          >
            {resultado.estado}
          </span>
        </div>
      </motion.div>
    );
  }

  if (tipo === "animalitos") {
    return (
      <motion.div
        initial={{ y: 8 }}
        animate={{ y: 0 }}
        className="flex items-center gap-4"
        data-testid="game-result-animalito"
      >
        <div
          className="flex h-20 w-20 items-center justify-center rounded-2xl text-3xl font-extrabold shadow-sm"
          style={{ background: "hsl(var(--game-surface))", color: "hsl(var(--game-accent))" }}
        >
          {String(resultado.numero)}
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Figura ganadora</p>
          <p className="font-display text-2xl font-bold" style={{ color: "hsl(var(--game-accent))" }}>
            {resultado.figura}
          </p>
          {resultado.comodin && (
            <span
              className="mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-bold"
              style={{ background: "hsl(var(--game-accent))", color: "hsl(var(--game-surface))" }}
              data-testid="game-result-comodin"
            >
              COMODÍN
            </span>
          )}
        </div>
      </motion.div>
    );
  }

  if (tipo === "raspaditos") {
    const win = resultado.premio && resultado.premio !== "Sin premio";
    return (
      <motion.div
        initial={{ scale: 0.92 }}
        animate={{ scale: 1 }}
        className="inline-flex flex-col items-center rounded-2xl border-2 px-8 py-6 shadow-sm"
        style={{
          borderColor: win ? "hsl(var(--game-accent))" : "hsl(var(--border))",
          background: "hsl(var(--game-surface))",
        }}
        data-testid="game-result-raspadito"
      >
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {win ? "¡Premio!" : "Resultado"}
        </span>
        <span
          className="font-display text-3xl font-extrabold"
          style={{ color: win ? "hsl(var(--game-accent))" : "hsl(var(--foreground))" }}
        >
          {resultado.premio}
        </span>
      </motion.div>
    );
  }

  return <span>{TIPO_LABEL[tipo]}</span>;
};
